/**
 * The 14 security buckets, and ONE colour each.
 *
 * These strings are `security_product_mapping.product_category` — the same
 * list the coverage heatmap draws and the purchase history tags rows with.
 * They are authored in the database, so this file is a VIEW of that list, not
 * the definition of it: an unknown category must render, not vanish.
 *
 * Why a shared map rather than a palette index per chart: the same category
 * has to be the same colour in every widget, or the board asks the reader to
 * re-learn the legend in each cell. CategorySpendBars already makes this
 * argument for vendors ("stable colors based on global vendor order"); this is
 * the same rule for the axis that does not move.
 *
 * The hues are ordered so the buckets a PANW rep cares about most — the
 * network-security ones the platform actually competes in — sit at the warm,
 * high-contrast end, and the adjacent-but-not-ours ones (Observability, Cloud)
 * sit cooler. That is a deliberate ranking, not decoration: on a feed sorted
 * by date, colour is the only thing carrying "is this mine to care about".
 *
 * MEASURED 2026-09-18, because the paragraph above was aspirational. Four
 * pairs sat under dE 15 — close enough that a reader cannot reliably tell them
 * apart — and the two the rep cares about MOST, Firewall and SSE, were four
 * degrees of hue apart at the COOL end, the opposite of what is claimed. Two
 * moves fixed it, chosen to serve the ranking rather than just the arithmetic:
 *
 *   Endpoint/EDR/XDR  #8b5cf6 -> #fde047   was 10.1 from SIEM, and one of
 *                                          three collisions in the indigo /
 *                                          violet pile-up. Core cyber, so it
 *                                          moves to the warm end.
 *   NDR               #06b6d4 -> #f472b6   was 14.6 from Observability. Core
 *                                          cyber too, so IT moves and
 *                                          Observability keeps the cool, quiet
 *                                          slot that says "not ours" —
 *                                          moving Observability instead would
 *                                          have put the one bucket we do not
 *                                          sell into the loudest colour.
 *
 * Minimum pairwise distance is now 17.4 and nothing is under 15. The binding
 * pair is Firewall vs SSE, which stay neighbours on purpose: they are the two
 * halves of the same platform story and read as a family.
 *
 * WHAT THIS DOES NOT FIX, and cannot: under deuteranopia the minimum is 1.7.
 * Thirteen categories plus a grey cannot be separated by hue alone for a
 * colour-blind reader — no palette of this size can. Colour must therefore
 * never be the ONLY channel: every surface that uses this map owes its
 * segments a name, a legend or a label. `securityCategoryShort` exists for
 * exactly that.
 */
export const SECURITY_CATEGORIES = [
    'NGFW/Firewall',
    'SSE/SASE',
    'Identity & Access',
    'Endpoint/EDR/XDR',
    'SIEM',
    'SOAR',
    'Email Security',
    'Data/DLP',
    'Vuln/Exposure Mgmt',
    'WAF/API Security',
    'NDR',
    'Observability',
    'Cloud Security',
    'Unmapped',
];
const COLORS = {
    'NGFW/Firewall': '#4f46e5',
    'SSE/SASE': '#6366f1',
    'Identity & Access': '#0ea5e9',
    'Endpoint/EDR/XDR': '#fde047',
    SIEM: '#a855f7',
    SOAR: '#d946ef',
    'Email Security': '#f43f5e',
    'Data/DLP': '#f97316',
    'Vuln/Exposure Mgmt': '#f59e0b',
    'WAF/API Security': '#14b8a6',
    NDR: '#f472b6',
    Observability: '#0891b2',
    'Cloud Security': '#10b981',
    // Deliberately grey, and the ONLY grey in the map: this is the bucket that
    // means "we could not classify it", which is absence of information. Grey is
    // never a value anywhere else on the board — a real $1.3B drawn in the
    // colour that means "nothing here" is how share-of-wallet came to render a
    // blank rectangle.
    Unmapped: '#94a3b8',
};
/** A category's colour. Unknown categories get the unmapped grey, never nothing. */
export function securityCategoryColor(category) {
    if (!category)
        return COLORS.Unmapped;
    return COLORS[category] || COLORS.Unmapped;
}
/** True for the buckets PANW's platform actually competes in. */
export function isCoreCyber(category) {
    return !!category && category !== 'Unmapped' && category !== 'Observability';
}
/**
 * Short forms, for places where the full name will not fit — the ring's own
 * segment labels most of all. "Vuln/Exposure Mgmt" set radially at 9px is a
 * smear; "Vuln" is a word. Only the names that actually need shortening appear
 * here, and `securityCategoryShort` returns the full name for everything else,
 * so a new category is legible by default rather than blank.
 */
const SHORT = {
    'NGFW/Firewall': 'Firewall',
    'SSE/SASE': 'SSE',
    'Identity & Access': 'Identity',
    'Endpoint/EDR/XDR': 'Endpoint',
    'Email Security': 'Email',
    'Data/DLP': 'DLP',
    'Vuln/Exposure Mgmt': 'Vuln',
    'WAF/API Security': 'WAF',
    'Cloud Security': 'Cloud',
};
/** A name that fits a chart label. Falls back to the full one. */
export function securityCategoryShort(category) {
    if (!category)
        return '';
    return SHORT[category] || category;
}
